import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import { ArrowBack, Edit, Refresh, Add } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { format, subDays } from 'date-fns';
import { adsetsService } from '../../services/adsets.service';
import { adsService, Ad } from '../../services/ads.service';
import { insightsService } from '../../services/insights.service';
import StatusBadge from '../../components/StatusBadge';
import { HistoryButton } from '../../components/EntityHistory';

interface UpdateAdSetRequest {
  name?: string;
  status?: string;
  daily_budget?: number;
  lifetime_budget?: number;
  bid_amount?: number;
}

const optimizationGoals = [
  { value: 'REACH', label: 'Reach' },
  { value: 'IMPRESSIONS', label: 'Impressions' },
  { value: 'LINK_CLICKS', label: 'Link Clicks' },
  { value: 'LANDING_PAGE_VIEWS', label: 'Landing Page Views' },
  { value: 'CONVERSIONS', label: 'Conversions' },
  { value: 'LEAD_GENERATION', label: 'Lead Generation' },
  { value: 'APP_INSTALLS', label: 'App Installs' },
  { value: 'VIDEO_VIEWS', label: 'Video Views' },
];

const billingEvents = [
  { value: 'IMPRESSIONS', label: 'Impressions (CPM)' },
  { value: 'LINK_CLICKS', label: 'Link Clicks (CPC)' },
  { value: 'APP_INSTALLS', label: 'App Installs' },
  { value: 'PAGE_LIKES', label: 'Page Likes' },
];

export default function AdSetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState<UpdateAdSetRequest>({});
  const [insightsDateRange] = useState({
    date_from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    date_to: format(new Date(), 'yyyy-MM-dd'),
  });

  const { data: adset, isLoading } = useQuery({
    queryKey: ['adset', id],
    queryFn: () => adsetsService.getById(id!),
    enabled: !!id,
  });

  const { data: adsResult, isLoading: isLoadingAds } = useQuery({
    queryKey: ['adset-ads', id],
    queryFn: () => adsService.query({ ad_set_id: id! }),
    enabled: !!id && tab === 0,
  });

  const ads: Ad[] = adsResult?.data ?? [];

  const { data: insights, isLoading: insightsLoading, refetch: refetchInsights } = useQuery({
    queryKey: ['adset-insights', id, insightsDateRange],
    queryFn: () => insightsService.getAdSetInsights(id!, insightsDateRange),
    enabled: !!id && tab === 1,
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAdSetRequest) => adsetsService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adset', id] });
      queryClient.invalidateQueries({ queryKey: ['adsets'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-adsets'] });
      setEditDialogOpen(false);
    },
  });


  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!adset) {
    return (
      <Box>
        <Alert severity="error">Ad Set not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/adsets')} sx={{ mt: 2 }}>
          Back to Ad Sets
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/campaigns/${adset.campaign_id}`)}
          sx={{ mr: 2 }}
        >
          Back to Campaign
        </Button>
        <Typography variant="h4" fontWeight={600} sx={{ flexGrow: 1 }}>
          {adset.name}
        </Typography>
        <Box sx={{ mr: 2 }}>
          <StatusBadge
            status={adset.status}
            effectiveStatus={adset.effective_status}
            staleSince={adset.status_stale_since}
          />
          <HistoryButton entityType="ad_sets" entityId={adset.id} entityName={adset.name} />
        </Box>
        <Button variant="contained" startIcon={<Edit />} onClick={() => navigate(`/adsets/${id}/edit`)}>
          Edit Ad Set
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Campaign
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {adset.campaign?.name || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Ad Account
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {adset.ad_account?.name || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Optimization Goal
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {optimizationGoals.find((g) => g.value === adset.optimization_goal)?.label || adset.optimization_goal}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Billing Event
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {billingEvents.find((e) => e.value === adset.billing_event)?.label || adset.billing_event}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Daily Budget
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {adset.daily_budget ? `$${Number(adset.daily_budget).toFixed(2)}` : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Lifetime Budget
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {adset.lifetime_budget ? `$${Number(adset.lifetime_budget).toFixed(2)}` : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Bid Amount
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {adset.bid_amount ? `$${(adset.bid_amount ).toFixed(2)}` : 'Automatic'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Start Date
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {adset.start_time ? new Date(adset.start_time).toLocaleDateString() : 'Immediately'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                End Date
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {adset.end_time ? new Date(adset.end_time).toLocaleDateString() : 'No end date'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {new Date(adset.created_at).toLocaleDateString()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Ads" />
        <Tab label="Insights" />
        <Tab label="Targeting" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate(`/ads/new?adset_id=${id}`)}
            >
              Create Ad
            </Button>
          </Box>
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Ad Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Headline</TableCell>
                    <TableCell>CTA</TableCell>
                    <TableCell>FB Ad ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingAds ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : ads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No ads yet. Create your first ad to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    ads.map((ad) => (
                      <TableRow
                        key={ad.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/ads/${ad.id}`)}
                      >
                        <TableCell>
                          <Typography fontWeight={500}>{ad.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={ad.status} effectiveStatus={ad.effective_status}
                            staleSince={ad.status_stale_since} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {ad.headline || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {ad.call_to_action?.replace('_', ' ') || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {ad.fb_ad_id || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Performance Metrics ({insightsDateRange.date_from} to {insightsDateRange.date_to})
              </Typography>
              <Button
                startIcon={<Refresh />}
                onClick={() => refetchInsights()}
                disabled={insightsLoading}
              >
                Refresh
              </Button>
            </Box>
            {insightsLoading ? (
              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Skeleton variant="rectangular" height={100} />
                  </Grid>
                ))}
              </Grid>
            ) : insights && insights.length > 0 ? (
              <>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Impressions
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {insights.reduce((sum, i) => sum + (i.impressions || 0), 0).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Clicks
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {insights.reduce((sum, i) => sum + (i.clicks || 0), 0).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Spend
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          ${(insights.reduce((sum, i) => sum + (i.spend || 0), 0) / 100).toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Reach
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {insights.reduce((sum, i) => sum + (i.reach || 0), 0).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          CTR
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {(() => {
                            const totalImpressions = insights.reduce((sum, i) => sum + (i.impressions || 0), 0);
                            const totalClicks = insights.reduce((sum, i) => sum + (i.clicks || 0), 0);
                            return totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
                          })()}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          CPC
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          ${(() => {
                            const totalSpend = insights.reduce((sum, i) => sum + (i.spend || 0), 0);
                            const totalClicks = insights.reduce((sum, i) => sum + (i.clicks || 0), 0);
                            return totalClicks > 0 ? ((totalSpend / 100) / totalClicks).toFixed(2) : '0.00';
                          })()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Typography color="text.secondary">
                No insights data available for this ad set. Sync data from your ad account to see metrics.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Targeting Configuration
            </Typography>
            {adset.targeting && Object.keys(adset.targeting).length > 0 ? (
              <Box
                component="pre"
                sx={{
                  bgcolor: 'action.hover',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.875rem',
                }}
              >
                {JSON.stringify(adset.targeting, null, 2)}
              </Box>
            ) : (
              <Typography color="text.secondary">
                No targeting configuration set. Default targeting will be used.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Ad Set Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Ad Set</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Ad Set Name"
              fullWidth
              value={editData.name || ''}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            />
            <FormControl fullWidth>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Status
              </Typography>
              <ToggleButtonGroup
                value={editData.status}
                exclusive
                onChange={(_, value) => value && setEditData({ ...editData, status: value })}
                fullWidth
              >
                <ToggleButton value="PAUSED">Paused</ToggleButton>
                <ToggleButton value="ACTIVE">Active</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
            <TextField
              label="Daily Budget"
              type="number"
              fullWidth
              value={editData.daily_budget || ''}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  daily_budget: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText="Leave empty for no daily limit"
            />
            <TextField
              label="Lifetime Budget"
              type="number"
              fullWidth
              value={editData.lifetime_budget || ''}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  lifetime_budget: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText="Total budget for ad set lifetime"
            />
            <TextField
              label="Bid Amount"
              type="number"
              fullWidth
              value={editData.bid_amount || ''}
              onChange={(e) =>
                setEditData({
                  ...editData,
                  bid_amount: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText="Leave empty for automatic bidding"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => updateMutation.mutate(editData)}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
